import { useContext, useMemo, useState } from 'react';
import { Button, Drawer, Grid, Space } from 'antd';
import { FilterOutlined } from '@ant-design/icons';

import { useApproval, useApprovalCount } from '../../../../Hooks/TT_ELD/NewAccounts';
import ApprovalTable from './CustomTable/ApprovalTable';
import SearchInput from '../../../../Utils/SearchInput';
import RefreshContext from '../../../utils/context';

const { useBreakpoint } = Grid;

type EmailVerifiedProps = {
  companyName: string;
  setCompanyName: (value: string) => void;
  username: string;
  setUsername: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  skip: string;
  setSkip: (value: string) => void;
  reload: any;
};

const EmailVerified = ({
  companyName,
  setCompanyName,
  username,
  setUsername,
  email,
  setEmail,
  skip,
  setSkip,
  reload,
}: EmailVerifiedProps) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data, refetch, isLoading, isFetching } = useApproval(
    username,
    email,
    companyName,
    skip,
    true,
  );

  const count = useApprovalCount();

  const onChange = (query: any) => setSkip(query.current);

  const handleChange = (event: string, inputName: string) => {
    const updateFunctions: Record<string, (value: string) => void> = {
      companyName: setCompanyName,
      email: setEmail,
      username: setUsername,
    };

    if (updateFunctions[inputName]) {
      updateFunctions[inputName](event.trim());
      setSkip('1');
    }
  };

  const { refetchBtn, setRefetchBtn } = useContext(RefreshContext);
  const RefToggler = () => setRefetchBtn(refetchBtn + 1);

  const updates = () => {
    refetch();
    reload();
    count.refetch();
    RefToggler();
  };

  const filtersUI = useMemo(
    () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
        <SearchInput
          value={companyName}
          onDebounceChange={(value: string) => handleChange(value, 'companyName')}
          placeholder="Search by company"
        />
        <SearchInput
          value={username}
          onDebounceChange={(value: string) => handleChange(value, 'username')}
          placeholder="Search by username"
        />
        <SearchInput
          value={email}
          onDebounceChange={(value: string) => handleChange(value, 'email')}
          placeholder="Search by email"
        />

        {isMobile && (
          <Space style={{ width: '100%' }} size={10}>
            <Button block onClick={() => setFiltersOpen(false)}>
              Close
            </Button>
            <Button
              block
              type="primary"
              onClick={() => {
                updates();
                setFiltersOpen(false);
              }}
            >
              Apply & Refresh
            </Button>
          </Space>
        )}
      </div>
    ),
    [companyName, username, email, isMobile],
  );

  return (
    <div className="mainPage">
      {/* Toolbar */}
      {!isMobile ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            marginBottom: 10,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <SearchInput
              value={companyName}
              onDebounceChange={(value: string) => handleChange(value, 'companyName')}
              placeholder="Search by company"
            />
            <SearchInput
              value={username}
              onDebounceChange={(value: string) => handleChange(value, 'username')}
              placeholder="Search by username"
            />
            <SearchInput
              value={email}
              onDebounceChange={(value: string) => handleChange(value, 'email')}
              placeholder="Search by email"
            />
          </div>

          <Button size="large" onClick={updates}>
            Refresh
          </Button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <Button
            block
            size="large"
            onClick={() => setFiltersOpen(true)}
            icon={<FilterOutlined />}
          />
          <Button block size="large" onClick={updates}>
            Refresh
          </Button>
        </div>
      )}

      {/* Mobile Drawer */}
      <Drawer
        title="Filters"
        placement="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        height="60vh"
      >
        {filtersUI}
      </Drawer>

      <ApprovalTable
        data={data?.data}
        onChange={onChange}
        skip={skip}
        total={data?.count}
        isLoading={isLoading}
        isFetching={isFetching}
        refetch={updates}
        currentTab="verified"
        reload={reload}
      />
    </div>
  );
};

export default EmailVerified;
